import {generateUuid, getCurrentIsoTimestamp} from "@vesta/global-common-typescript/utilities/utilities";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {
    type UserDefinedObjectTemplateAdditionalColumn,
    UserDefinedObjectTemplateAdditionalColumnType as Type,
    UserDefinedObjectTemplateAdditionalColumnSourceType as Source,
    userDefinedObjectTemplateAdditionalColumnValueValidatorMap,
} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";

export type UserDefinedObjectColumnValueContext = {
    formatTimestamp?: (value: Iso8601DateTime) => string;
    resolveGeography?: (type: Type, value: unknown) => string | null;
    resolveOrganizationalUnit?: (id: Uuid) => string;
    resolveProduct?: (id: Uuid) => string;
    resolveUser?: (id: Uuid) => string;
    resolveUserTemplate?: (id: Uuid) => string;
    referenceOptionsByTemplateId?: ReadonlyMap<Uuid, ReadonlyArray<{id: Uuid; displayName: string}>>;
};

type ColumnValueDetails = {displayName: string; defaultValue: () => unknown};
/** Every shared UDO type has a UI descriptor; capability-specific type IDs are never translated here. */
export const userDefinedObjectAdditionalColumnDetailsMap: Record<Type, ColumnValueDetails> = {
    [Type.boolean]: {displayName: "Boolean", defaultValue: () => null},
    [Type.email]: {displayName: "Email", defaultValue: () => ""},
    [Type.geographyCountries]: {displayName: "Country", defaultValue: () => null},
    [Type.geographyIndiaCities]: {displayName: "City", defaultValue: () => null},
    [Type.geographyIndiaPincodes]: {displayName: "Pincode", defaultValue: () => null},
    [Type.geographyIndiaStates]: {displayName: "State", defaultValue: () => null},
    [Type.geographyIndiaZones]: {displayName: "Zone", defaultValue: () => null},
    [Type.geographyLatitude]: {displayName: "Latitude", defaultValue: () => null},
    [Type.geographyLongitude]: {displayName: "Longitude", defaultValue: () => null},
    [Type.image]: {displayName: "Image", defaultValue: () => null},
    [Type.indianPhoneNumber]: {displayName: "Phone number", defaultValue: () => ""},
    [Type.integer]: {displayName: "Integer", defaultValue: () => null},
    [Type.json]: {displayName: "JSON", defaultValue: () => null},
    [Type.number]: {displayName: "Number", defaultValue: () => null},
    [Type.ou]: {displayName: "Organizational unit", defaultValue: () => null},
    [Type.ous]: {displayName: "Organizational units", defaultValue: () => []},
    [Type.product]: {displayName: "Product", defaultValue: () => null},
    [Type.string]: {displayName: "Text", defaultValue: () => ""},
    [Type.timestamp]: {displayName: "Timestamp", defaultValue: getCurrentIsoTimestamp},
    [Type.user]: {displayName: "User", defaultValue: () => null},
    [Type.userDefinedObject]: {displayName: "User defined object", defaultValue: () => null},
    [Type.userDefinedObjects]: {displayName: "User defined objects", defaultValue: () => []},
    [Type.userTemplate]: {displayName: "Role", defaultValue: () => null},
    [Type.uuid]: {displayName: "UUID", defaultValue: generateUuid},
};

function referenceLabel(value: unknown, resolve?: (id: Uuid) => string): string {
    const id = uuidZodValidator.safeParse(value);
    if (!id.success) {
        return "?";
    }
    return resolve?.(id.data) ?? "?";
}

export function formatUserDefinedObjectAdditionalColumnValue(
    column: UserDefinedObjectTemplateAdditionalColumn,
    value: unknown,
    context: UserDefinedObjectColumnValueContext = {},
): string {
    if (value === null || value === undefined) {
        return "-";
    }
    switch (column.type) {
        case Type.json:
            return JSON.stringify(value) ?? "-";
        case Type.boolean:
            return typeof value === "boolean" ? (value ? "Yes" : "No") : "-";
        case Type.timestamp: {
            const timestamp = iso8601DateTimeZodValidator.safeParse(value);
            return timestamp.success ? (context.formatTimestamp?.(timestamp.data) ?? timestamp.data) : "-";
        }
        case Type.user:
            return referenceLabel(value, context.resolveUser);
        case Type.userTemplate:
            return referenceLabel(value, context.resolveUserTemplate);
        case Type.ou:
            return referenceLabel(value, context.resolveOrganizationalUnit);
        case Type.product:
            return referenceLabel(value, context.resolveProduct);
        case Type.ous:
            return Array.isArray(value) && value.length > 0 ? value.map((id) => referenceLabel(id, context.resolveOrganizationalUnit)).join(", ") : "-";
        case Type.userDefinedObject:
        case Type.userDefinedObjects: {
            const values = column.type === Type.userDefinedObjects ? (Array.isArray(value) ? value : []) : [value];
            const options = context.referenceOptionsByTemplateId?.get(column.configuration.userDefinedObjectTemplateId) ?? [];
            return values.length > 0 ? values.map((id) => options.find((option) => option.id === id)?.displayName ?? "?").join(", ") : "-";
        }
        case Type.geographyCountries:
        case Type.geographyIndiaCities:
        case Type.geographyIndiaPincodes:
        case Type.geographyIndiaStates:
        case Type.geographyIndiaZones:
        case Type.geographyLatitude:
        case Type.geographyLongitude:
            return context.resolveGeography?.(column.type, value) ?? (typeof value === "number" ? String(value) : "-");
        case Type.integer:
        case Type.number:
            return typeof value === "number" && Number.isFinite(value) ? String(value) : "-";
        case Type.email:
        case Type.image:
        case Type.indianPhoneNumber:
        case Type.string:
        case Type.uuid:
            return typeof value === "string" ? value : "-";
    }
}

export type UserDefinedObjectColumnInputResult = {success: true; value: unknown} | {success: false; message: string};

/** Parse UI text without turning malformed JSON/numbers/references into stored strings. */
export function parseUserDefinedObjectAdditionalColumnInput(
    column: UserDefinedObjectTemplateAdditionalColumn,
    input: string | ReadonlyArray<string>,
): UserDefinedObjectColumnInputResult {
    const required = column.source.type === Source.physical && column.source.required;
    const empty = typeof input === "string" ? input.trim() === "" : input.length === 0;
    if (empty) {
        if (required) {
            return {success: false, message: "This field is required."};
        }
        const text = column.type === Type.string || column.type === Type.email || column.type === Type.indianPhoneNumber || column.type === Type.image;
        return {success: true, value: text ? input : column.type === Type.ous || column.type === Type.userDefinedObjects ? [] : null};
    }
    let value: unknown;
    try {
        switch (column.type) {
            case Type.json:
                value = typeof input === "string" ? JSON.parse(input) : input;
                break;
            case Type.boolean:
                if (input !== "true" && input !== "false") {
                    return {success: false, message: "Choose Yes or No."};
                }
                value = input === "true";
                break;
            case Type.number:
            case Type.integer:
            case Type.geographyLatitude:
            case Type.geographyLongitude:
                value = typeof input === "string" ? Number(input) : Number.NaN;
                break;
            case Type.ous:
            case Type.userDefinedObjects:
                value = Array.isArray(input) ? input : [input];
                break;
            case Type.email:
            case Type.geographyCountries:
            case Type.geographyIndiaCities:
            case Type.geographyIndiaPincodes:
            case Type.geographyIndiaStates:
            case Type.geographyIndiaZones:
            case Type.image:
            case Type.indianPhoneNumber:
            case Type.ou:
            case Type.product:
            case Type.string:
            case Type.timestamp:
            case Type.user:
            case Type.userDefinedObject:
            case Type.userTemplate:
            case Type.uuid:
                value = input;
        }
    } catch {
        return {success: false, message: "Enter valid JSON."};
    }
    if (value === null && required) {
        return {success: false, message: "This field is required."};
    }
    const result = userDefinedObjectTemplateAdditionalColumnValueValidatorMap[column.type].safeParse(value);
    return result.success
        ? {success: true, value: result.data}
        : {success: false, message: `Enter a valid ${userDefinedObjectAdditionalColumnDetailsMap[column.type].displayName.toLowerCase()}.`};
}
